import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { FocusTrapDirective } from '../directives/focus-trap.directive';

/**
 * Slim "Name this copy" modal that pops on every duplicate trigger
 * (dashboard listing card, wizard "Save a copy" pill, resume-draft-card
 * Duplicate ghost). Auto-focuses the input, Enter saves, Esc cancels.
 *
 * The host owns the duplicate side-effect; this component just collects a
 * title or signals cancel.
 */
@Component({
  selector: 'cnt-duplicate-rename-modal',
  standalone: true,
  imports: [FormsModule, FocusTrapDirective],
  templateUrl: './duplicate-rename-modal.component.html',
})
export class DuplicateRenameModalComponent implements AfterViewInit, OnDestroy {
  /** Pre-filled title — typically `HostListingDraftService.suggestCopyTitle(...)`. */
  @Input() set defaultTitle(v: string) { this.value = v ?? ''; }
  /** Sub-label below the headline; lets the caller hint at intent
   *  ("Copying Heritage Oak Vineyard." / "Saving a copy of your draft."). */
  @Input() subtitle = 'Pick a name that fits your new site.';
  /** When true, render a secondary "Save to drafts" action alongside the
   *  primary "Save copy & open" button. Used on the dashboard listing-card
   *  duplicate flow so a host can stash a copy without leaving /hosting. */
  @Input() showSaveToDrafts = false;
  /** When true, render a tertiary "Or publish as-is →" text link under the
   *  button row. Only valid when the source is a real published listing so
   *  the clone is guaranteed to pass publish-validation. */
  @Input() showQuickPublish = false;

  @Output() saved = new EventEmitter<string>();
  @Output() savedToDrafts = new EventEmitter<string>();
  @Output() quickPublished = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild('input', { static: true }) inputEl?: ElementRef<HTMLInputElement>;

  value = '';
  readonly titleId = `dup-rename-${Math.random().toString(36).slice(2, 8)}`;

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      const el = this.inputEl?.nativeElement;
      if (!el) return;
      el.focus();
      el.select();
    });
  }

  ngOnDestroy(): void { /* noop — focus trap handles its own cleanup */ }

  save(): void {
    const trimmed = this.value.trim();
    if (!trimmed) return;
    this.saved.emit(trimmed);
  }

  saveToShelf(): void {
    const trimmed = this.value.trim();
    if (!trimmed) return;
    this.savedToDrafts.emit(trimmed);
  }

  quickPublish(): void {
    const trimmed = this.value.trim();
    if (!trimmed) return;
    this.quickPublished.emit(trimmed);
  }

  cancel(): void { this.cancelled.emit(); }
}
